import { useState, useEffect, useRef } from "react";
import "./Contact.css";
import Header from "../Header/Header";

/* ── Particle canvas background ── */
function ContactParticles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    let W, H, raf;
    const resize = () => {
      W = c.width = window.innerWidth;
      H = c.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const dust = Array.from({ length: 180 }, () => ({
      x: Math.random() * 1600,
      y: Math.random() * 1400,
      r: Math.random() * 1.2 + 0.2,
      o: Math.random() * 0.5 + 0.08,
      vx: (Math.random() - 0.5) * 0.11,
      vy: (Math.random() - 0.5) * 0.11,
      f: Math.random() * Math.PI * 2,
    }));
    const bokeh = Array.from({ length: 10 }, () => ({
      x: Math.random() * 1600,
      y: Math.random() * 1400,
      r: Math.random() * 40 + 14,
      o: Math.random() * 0.09 + 0.02,
      vx: (Math.random() - 0.5) * 0.06,
      vy: (Math.random() - 0.5) * 0.05,
      p: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      bokeh.forEach((b) => {
        b.x += b.vx;
        b.y += b.vy;
        b.p += 0.007;
        if (b.x < -60) b.x = W + 60;
        if (b.x > W + 60) b.x = -60;
        if (b.y < -60) b.y = H + 60;
        if (b.y > H + 60) b.y = -60;
        const op = b.o * (0.6 + 0.4 * Math.sin(b.p));
        const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        g.addColorStop(0, `rgba(212,175,55,${op})`);
        g.addColorStop(1, "rgba(212,175,55,0)");
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });
      dust.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.f += 0.022;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${p.o * (0.5 + 0.5 * Math.sin(p.f))})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={canvasRef} className="ct-particles" />;
}

/* ── Validation rules ── */
const RULES = {
  fullName: { required: true, min: 2, label: "Full name" },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    label: "Email",
  },
  phone: {
    required: true,
    pattern: /^[+\d\s\-()]{7,20}$/,
    label: "Phone number",
  },
  country: { required: true, label: "Country" },
  city: { required: true, min: 2, label: "City" },
  sponsorId: {
    required: false,
    pattern: /^(GBI-\d{4,8})?$/,
    label: "Sponsor ID",
  },
  message: { required: true, min: 10, label: "Message" },
};

function validate(name, value) {
  const r = RULES[name];
  if (!r) return { ok: true, msg: "" };
  const v = value.trim();
  if (r.required && !v) return { ok: false, msg: "This field is required" };
  if (!r.required && !v) return { ok: true, msg: "" };
  if (r.min && v.length < r.min)
    return {
      ok: false,
      msg: `${r.label} must be at least ${r.min} characters`,
    };
  if (r.pattern && !r.pattern.test(v))
    return { ok: false, msg: `Please enter a valid ${r.label.toLowerCase()}` };
  return { ok: true, msg: "✓ Looks good" };
}

const COUNTRIES = [
  "Pakistan",
  "Saudi Arabia",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "Qatar",
  "Kuwait",
  "Germany",
  "Other",
];

const SOCIAL_LINKS = [
  { label: "FB", href: "#", title: "Facebook" },
  { label: "IG", href: "#", title: "Instagram" },
  { label: "WA", href: "https://wa.me/923000000000", title: "WhatsApp" },
  { label: "YT", href: "#", title: "YouTube" },
  { label: "TK", href: "#", title: "TikTok" },
];

const OFFICE_HOURS = [
  { day: "Mon – Thu", time: "9:00am – 7:00pm" },
  { day: "Friday", time: "9:00am – 1:00pm" },
  { day: "Saturday", time: "10:00am – 5:00pm" },
  { day: "Sunday", time: "Closed" },
];

const INITIAL = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  sponsorId: "",
  message: "",
};

export default function Contact2() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [charCount, setCharCount] = useState(0);

  /* live validation on touched fields */
  useEffect(() => {
    const next = {};
    Object.keys(touched).forEach((k) => {
      if (touched[k]) next[k] = validate(k, form[k]);
    });
    setErrors(next);
  }, [form, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "message") setCharCount(value.length);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    /* touch all fields to reveal errors */
    const allTouched = Object.keys(INITIAL).reduce(
      (acc, k) => ({ ...acc, [k]: true }),
      {},
    );
    setTouched(allTouched);

    const allErrors = Object.keys(INITIAL).reduce((acc, k) => {
      acc[k] = validate(k, form[k]);
      return acc;
    }, {});
    setErrors(allErrors);

    const hasError = Object.values(allErrors).some((v) => !v.ok);
    if (hasError) return;

    setStatus("loading");
    setTimeout(() => setStatus("success"), 1800);
  };

  const handleReset = () => {
    setForm(INITIAL);
    setErrors({});
    setTouched({});
    setStatus("idle");
    setCharCount(0);
  };

  const fieldClass = (name) => {
    if (!touched[name]) return "";
    return errors[name]?.ok === false
      ? "error"
      : errors[name]?.ok
        ? "valid"
        : "";
  };

  return (
    <div className="ct-page">
      <Header />
      <ContactParticles />

      {/* ── Hero banner ── */}
      <div className="ct-hero">
        <span className="ct-label">Get In Touch</span>
        <h1 className="ct-title">
          Contact <span className="ct-gold">Us</span>
        </h1>
        <div className="ct-gline" />
        <p className="ct-sub">
          Have questions about investing or joining our network? We're here to
          guide you every step of the way.
        </p>
      </div>

      {/* ── Body grid ── */}
      <div className="ct-body">
        {/* Left info panel */}
        <div className="ct-info">
          <div className="ct-info-card">
            <div className="ct-card-title">Reach Us Directly</div>

            {[
              {
                icon: "📍",
                label: "Head Office",
                value: (
                  <>
                    Office #114, Plot no C9 B1 Sauleh Chamber Main Avenu,
                    <br />
                    S.I.T.E Aria, Karachi
                  </>
                ),
              },
              {
                icon: "📧",
                label: "Email",
                value: (
                  <a href="mailto:info@growbizint.com">info@growbizint.com</a>
                ),
              },
            ].map((c) => (
              <div key={c.label} className="ct-contact-row">
                <div className="ct-ci">{c.icon}</div>
                <div>
                  <div className="ct-cl">{c.label}</div>
                  <div className="ct-cv">{c.value}</div>
                </div>
              </div>
            ))}

            <div className="ct-contact-row">
              <div className="ct-ci">🌐</div>
              <div>
                <div className="ct-cl">Follow Us</div>
                <div className="ct-social-row">
                  {SOCIAL_LINKS.map((s) => (
                    <a
                      key={s.title}
                      href={s.href}
                      className="ct-soc"
                      title={s.title}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="ct-info-card">
            <div className="ct-card-title">Office Hours</div>
            <div className="ct-hours">
              {OFFICE_HOURS.map((h) => (
                <div key={h.day} className="ct-hour-row">
                  <span className="ct-hour-day">{h.day}</span>
                  <span>{h.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form panel */}
        <div className="ct-form-wrap">
          {status === "success" ? (
            <div className="ct-success">
              <div className="ct-success-icon">✦</div>
              <h3>Message Sent Successfully!</h3>
              <p>
                Thank you for reaching out. Our team will contact you within 24
                hours on your provided email or WhatsApp.
              </p>
              <button className="ct-success-back" onClick={handleReset}>
                Send Another Message
              </button>
            </div>
          ) : (
            <>
              <div className="ct-form-header">
                <h3>Send Us a Message</h3>
                <p>
                  Fill in your details and we'll get back to you within 24
                  hours.
                </p>
              </div>

              <form className="ct-form" onSubmit={handleSubmit} noValidate>
                {/* Row 1 — Full Name + Email */}
                <div className="form-row">
                  <Field
                    label="Full Name"
                    required
                    name="fullName"
                    className={fieldClass("fullName")}
                    msg={errors.fullName}
                  >
                    <input
                      type="text"
                      name="fullName"
                      id="fullName"
                      value={form.fullName}
                      placeholder="e.g. Ahmad Raza"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass("fullName")}
                      autoComplete="name"
                    />
                  </Field>

                  <Field
                    label="Email Address"
                    required
                    name="email"
                    className={fieldClass("email")}
                    msg={errors.email}
                  >
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={form.email}
                      placeholder="you@example.com"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass("email")}
                      autoComplete="email"
                    />
                  </Field>
                </div>

                {/* Row 2 — Phone + Country */}
                <div className="form-row">
                  <Field
                    label="Phone Number"
                    required
                    name="phone"
                    className={fieldClass("phone")}
                    msg={errors.phone}
                  >
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={form.phone}
                      placeholder="+92 300 0000000"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass("phone")}
                      autoComplete="tel"
                    />
                  </Field>

                  <Field
                    label="Country"
                    required
                    name="country"
                    className={fieldClass("country")}
                    msg={errors.country}
                  >
                    <select
                      name="country"
                      id="country"
                      value={form.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass("country")}
                    >
                      <option value="" disabled>
                        Select your country
                      </option>
                      {COUNTRIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                {/* Row 3 — City + Sponsor ID */}
                <div className="form-row">
                  <Field
                    label="City"
                    required
                    name="city"
                    className={fieldClass("city")}
                    msg={errors.city}
                  >
                    <input
                      type="text"
                      name="city"
                      id="city"
                      value={form.city}
                      placeholder="e.g. Karachi"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass("city")}
                    />
                  </Field>

                  <Field
                    label="Sponsor ID"
                    name="sponsorId"
                    className={fieldClass("sponsorId")}
                    msg={errors.sponsorId}
                    hint="Optional — leave blank if you don't have one yet"
                  >
                    <input
                      type="text"
                      name="sponsorId"
                      id="sponsorId"
                      value={form.sponsorId}
                      placeholder="e.g. GBI-001234"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass("sponsorId")}
                    />
                  </Field>
                </div>

                {/* Row 4 — Message */}
                <div className="form-row full">
                  <Field
                    label="Your Message"
                    required
                    name="message"
                    className={fieldClass("message")}
                    msg={errors.message}
                  >
                    <div className="textarea-wrap">
                      <textarea
                        name="message"
                        id="message"
                        value={form.message}
                        maxLength={500}
                        placeholder="Tell us how we can help you — investment queries, membership info, or anything else..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={fieldClass("message")}
                      />
                      <span
                        className={`char-count${charCount > 450 ? " warn" : ""}`}
                      >
                        {charCount} / 500
                      </span>
                    </div>
                  </Field>
                </div>

                <button
                  type="submit"
                  className="ct-submit"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? (
                    <span className="btn-inner">
                      <span className="spinner" /> Sending...
                    </span>
                  ) : (
                    <span className="btn-inner">Send Message</span>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Field wrapper component ── */
function Field({ label, required, name, msg, hint, children }) {
  return (
    <div className="form-group">
      <label htmlFor={name} className="contact-field-label">
        {label} {required && <span className="req-star">*</span>}
      </label>
      <div className="input-wrap">{children}</div>
      {hint && <span className="field-hint">{hint}</span>}
      {msg && (
        <span className={`field-msg ${msg.ok ? "ok" : "err"}`}>{msg.msg}</span>
      )}
    </div>
  );
}
